import React, { useEffect, useRef, useState } from "react";
import { whatsappLink } from "@/lib/config";
import { useLanguage } from "@/context/LanguageContext";
import { MessageCircle, Play, Pause } from "lucide-react";

// 5-second voice teaser (Lionel, ElevenLabs). One MP3 per language.
const PREVIEW_URL = (lang) => `/audio/whatsapp-preview-${lang}.mp3`;

const HINT = {
  pt: "Ouça meu recado (5s)",
  en: "Listen to my note (5s)",
  fr: "Écoute mon message (5s)",
};

const WhatsAppButton = () => {
  const { t, lang } = useLanguage();
  const [hasPreview, setHasPreview] = useState(false);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const closeTimer = useRef(null);

  const previewLang = ["pt", "en"].includes(lang) ? lang : "fr";
  const previewSrc = PREVIEW_URL(previewLang);

  // Only reveal the preview UI if the MP3 is actually served (HEAD probe).
  useEffect(() => {
    let cancelled = false;
    fetch(previewSrc, { method: "HEAD" })
      .then((r) => {
        if (!r.ok) return false;
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        return ct.startsWith("audio/");
      })
      .then((ok) => !cancelled && setHasPreview(ok))
      .catch(() => !cancelled && setHasPreview(false));
    return () => {
      cancelled = true;
    };
  }, [previewSrc]);

  const play = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 400);
  };

  const openNow = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  return (
    <div
      data-floating="true"
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2"
      onMouseEnter={hasPreview ? openNow : undefined}
      onMouseLeave={hasPreview ? scheduleClose : undefined}
    >
      {hasPreview && open && (
        <div
          role="dialog"
          aria-label={HINT[lang] || HINT.pt}
          data-testid="whatsapp-preview-card"
          className="w-[min(260px,calc(100vw-3.5rem))] rounded-xl border border-[#e5e3db] bg-white shadow-[0_8px_24px_-10px_rgba(30,42,90,0.30)] p-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#25D366] font-medium">
            Prof Lionel
          </p>
          <p className="mt-0.5 text-xs text-[#1e2a5a] leading-snug">{HINT[lang] || HINT.pt}</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={play}
              data-testid="whatsapp-preview-play"
              aria-label={playing ? "pause" : "play"}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors"
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-[1px]" />}
            </button>
            <div className="flex-1 h-1 rounded-full bg-[#e5e3db] overflow-hidden">
              <div
                className="h-full bg-[#25D366] transition-all"
                style={{
                  width: playing ? "100%" : "0%",
                  transitionDuration: playing ? "5s" : "0.3s",
                }}
              />
            </div>
          </div>
        </div>
      )}
      <a
        href={whatsappLink()}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-floating-button"
        aria-label={t.contact.whatsapp}
        onFocus={hasPreview ? openNow : undefined}
        onBlur={hasPreview ? scheduleClose : undefined}
        onTouchStart={hasPreview ? openNow : undefined}
        className="wa-pulse inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:scale-[1.03]"
      >
        <MessageCircle className="h-5 w-5" strokeWidth={2} />
        <span className="hidden sm:inline">{t.contact.whatsapp}</span>
      </a>
      {hasPreview && (
        <audio
          ref={audioRef}
          src={previewSrc}
          preload="none"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />
      )}
    </div>
  );
};

export default WhatsAppButton;
